import {CodeUniqueConfig, Config} from "./config";
import parse from 'parse-css-color'
import {
    FillPatternTypeEnum,
    GeometryTypeEnum,
    LabelsPositionEnum,
    LegendOrientationEnum,
    LinePatternTypeEnum,
    StyleRuleTypeEnum
} from "../enum/enum";
import {isInteger, isNumber, nn, nnTrim, prefixUrl} from "../util";

/**
 * Verify if the string representation of a CSS colour is valid
 * @param colourStr {string} ex: 'black', 'rgb(0,0,0)', rgba(223,111,222,0.4)
 * @return {boolean} true if valid
 */
function verifyColour(colourStr) {
    try {
        const parsedColour = parse(colourStr);
        return !!parsedColour;
    } catch (e) {
        return false;
    }
}

/**
 * A layer style. Can be applied to render vector layers and to render legends for any layer
 * @extends CodeUniqueConfig
 * @hideconstructor
 */
class StyleConfig extends CodeUniqueConfig {
    getClassName(){
        return "Style";
    }

    /**
     * This style geometry type
     * @return {GeometryTypeEnum}
     */
    getGeometryType() {
        return GeometryTypeEnum.getForCode(this.config.geometryType);
    }

    /**
     * The attribute of this style (ex: the value it will look for to paint the correct colour in a discrete style)
     * @return {string}
     */
    getAttribute() {
        return this.config.attribute;
    }

    /**
     * Get the legend config for this style
     * @return {Legend}
     */
    getLegend() {
        if (this.config.legend) {
            return new Legend(this.config.legend);
        }
    }

    /**
     * Get the rules of this style
     * @return {Array<StyleRule>}
     */
    getRules() {
        let styleRules = [];
        if (this.config.styleRules) {
            for (let sr in this.config.styleRules) {
                styleRules.push(new StyleRule(this.config.styleRules[sr]));
            }
        }
        return styleRules;
    }

    verify() {
        let errors = super.verify();

        if (!this.config.geometryType || this.config.geometryType.length === 0) {
            errors.push(this.getLogId() + ": geometryType is mandatory");
        } else if (!GeometryTypeEnum.exists(this.config.geometryType)) {
            errors.push(this.getLogId() + ": invalid geometryType. Possible values: " + GeometryTypeEnum.getPossibleCodes());
        }

        const legend = this.getLegend();
        if (legend) {
            errors = errors.concat(legend.verify());
        }

        const rules = this.getRules();
        if (rules.length === 0) {
            errors.push(this.getLogId() + ": styleRules is mandatory");
        } else {
            for (let r in rules) {
                errors = errors.concat(rules[r].verify());
            }
        }

        return errors;
    }
}

/**
 * Legend config
 * @extends Config
 * @hideconstructor
 */
class Legend extends Config {
    getClassName(){
        return "Legend";
    }

    /**
     * A text description for the legend (can be formatted)
     * @return {string}
     */
    getLegendDescription() {
        return nnTrim(this.config.description);
    }

    /**
     * Labels positioning config
     * @return {LabelsPositionEnum}
     */
    getLabelsPosition() {
        return LabelsPositionEnum.getForCode(this.config.labelsPosition);
    }

    /**
     * Legend orientation config
     * @return {LegendOrientationEnum}
     */
    getLegendOrientation() {
        const orientation = LegendOrientationEnum.getForCode(this.config.orientation);
        return orientation ? orientation : LegendOrientationEnum.HOIZONTAL;
    }

    verify() {
        const errors = super.verify();

        if (this.config.labelsPosition && this.config.labelsPosition.trim().length > 0
            && !LabelsPositionEnum.exists(this.config.labelsPosition)) {
            errors.push(this.getLogId() + ": invalid labels position. Possible values: " + LabelsPositionEnum.getPossibleCodes());
        }

        if (this.config.orientation && this.config.orientation.trim().length > 0
            && !LegendOrientationEnum.exists(this.config.orientation)) {
            errors.push(this.getLogId() + ": invalid orientation. Possible values: " + LegendOrientationEnum.getPossibleCodes());
        }

        return errors;
    }
}

/**
 * Style rule
 * @extends CodeUniqueConfig
 * @hideconstructor
 */
class StyleRule extends CodeUniqueConfig {
    getClassName(){
        return "StyleRule";
    }

    isNoData(){

    }

    /**
     * Should this rule be hidden for the legend?
     * @return {boolean}
     */
    shouldHideForLegend(){
        return !!this.config.hideForLegend;
    }

    /**
     * Text label for this rule (translation key). Only for legend
     * @return {string}
     */
    getLabel() {
        return nnTrim(this.config.label);
    }

    /**
     * Type of this rule
     * @return {StyleRuleTypeEnum}
     */
    getStyleRuleType() {
        return StyleRuleTypeEnum.getForCode(this.config.type);
    }

    /**
     * This rule's buckets (only if type is DISCRETE)
     * @return {Array<Bucket>}
     */
    getBuckets() {
        let buckets = [];
        if (this.getStyleRuleType() === StyleRuleTypeEnum.DISCRETE && this.config.buckets) {
            for (let b in this.config.buckets) {
                buckets.push(new Bucket(this.config.buckets[b]));
            }
        }
        return buckets;
    }

    /**
     * This rule's ranges (only if type is CONTINUOUS)
     * @return {Array<Range>}
     */
    getRanges() {
        let ranges = [];
        if (this.getStyleRuleType() === StyleRuleTypeEnum.CONTINUOUS && this.config.ranges) {
            for (let r in this.config.ranges) {
                ranges.push(new Range(this.config.ranges[r]));
            }
        }
        return ranges;
    }

    /**
     * This rule's classes (only if type is CLASSIFIED)
     * @return {Array<Clazz>}
     */
    getClasses() {
        let classes = [];
        if (this.getStyleRuleType() === StyleRuleTypeEnum.CLASSIFIED && this.config.classes) {
            for (let c in this.config.classes) {
                classes.push(new Clazz(this.config.classes[c]));
            }
        }
        return classes;
    }

    /**
     * A CSS colour for the background. Only for SIMPLE, NODATA and CLUSTER rule types
     * @return {string}
     */
    getBackgroundColour() {
        const styleRuleType = this.getStyleRuleType();
        if (styleRuleType === StyleRuleTypeEnum.NODATA
            || styleRuleType === StyleRuleTypeEnum.CLUSTER
            || styleRuleType === StyleRuleTypeEnum.SIMPLE) {
            return this.config.backgroundColor;
        }
    }

    /**
     * A CSS colour for the border. Only for SIMPLE and NODATA rule types
     * @return {string}
     */
    getBorderColour() {
        const styleRuleType = this.getStyleRuleType();
        if (styleRuleType === StyleRuleTypeEnum.NODATA || styleRuleType === StyleRuleTypeEnum.CLUSTER || styleRuleType === StyleRuleTypeEnum.SIMPLE) {
            return this.config.borderColor;
        }
    }

    /**
     * A border width. Only for SIMPLE and NODATA rule types
     * @return {number}
     */
    getBorderWidth() {
        const styleRuleType = this.getStyleRuleType();
        if (styleRuleType === StyleRuleTypeEnum.NODATA || styleRuleType === StyleRuleTypeEnum.CLUSTER || styleRuleType === StyleRuleTypeEnum.SIMPLE) {
            return this.config.borderWidth;
        }
    }

    /**
     * A radius for points. Only for SIMPLE and NODATA rule types on styles with geometry type POINT
     * @return {number}
     */
    getRadius() {
        const styleRuleType = this.getStyleRuleType();
        if (styleRuleType === StyleRuleTypeEnum.NODATA || styleRuleType === StyleRuleTypeEnum.SIMPLE) {
            return this.config.radius === undefined ? undefined : Math.min(50, Math.max(2, this.config.radius));
        }
    }

    /**
     * A width for line. Only for SIMPLE and NODATA rule types on styles with geometry type LINE
     * @return {number}
     */
    getWidth() {
        const styleRuleType = this.getStyleRuleType();
        if (styleRuleType === StyleRuleTypeEnum.NODATA || styleRuleType === StyleRuleTypeEnum.SIMPLE) {
            return this.config.width === undefined ? undefined : Math.min(20, Math.max(1, this.config.width));
        }
    }

    /**
     * A fill pattern. Only for SIMPLE, NODATA rule types
     * @return {FillPattern}
     */
    getFillPattern() {
        const styleRuleType = this.getStyleRuleType();
        if (styleRuleType === StyleRuleTypeEnum.NODATA || styleRuleType === StyleRuleTypeEnum.SIMPLE || styleRuleType === StyleRuleTypeEnum.CLASSIFIED) {
            return this.config.fill === undefined ? undefined : new FillPattern(this.config.fill);
        }
    }

    /**
     * A line pattern. Only for SIMPLE, NODATA
     * Applicable to lines and polygons
     * @return {LinePattern}
     */
    getLinePattern() {
        const styleRuleType = this.getStyleRuleType();
        if (styleRuleType === StyleRuleTypeEnum.NODATA || styleRuleType === StyleRuleTypeEnum.SIMPLE) {
            return this.config.linePattern === undefined ? undefined : new LinePattern(this.config.linePattern);
        }
    }

    /**
     * An image url. Only for EXTERNAL_IMAGE and EXTERNAL_DYNAMIC_IMAGE rule types
     * @return {string}
     */
    getImageUrl() {
        return prefixUrl(this.config.imageUrl);
    }

    /**
     * An array of image urls associated with xoom levels. Only for ZOOM_DEPENDENT_EXTERNAL_DYNAMIC_IMAGE rule type
     * @return {Array<Object>}
     */
    getImageUrls() {
        return this.config.imageUrls;
    }

    /**
     * An array of image urls associated with xoom levels. Only for ZOOM_DEPENDENT_EXTERNAL_DYNAMIC_IMAGE rule type
     * @return {Array<Object>}
     */
    getImageUrlForZoomLevel(zoomLevel) {
        return prefixUrl(this.getImageUrls().filter(iuzl=>iuzl.zoomLevels.indexOf(zoomLevel)>=0)[0].imageUrl);
    }

    verify() {
        let errors = super.verify();

        if (!this.config.type || this.config.type.length === 0) {
            errors.push(this.getLogId() + ": type is mandatory");
        } else if (!StyleRuleTypeEnum.exists(this.config.type)) {
            errors.push(this.getLogId() + ": invalid type. Possible values: " + StyleRuleTypeEnum.getPossibleCodes());
        }
        const styleRuleType = this.getStyleRuleType();
        if (styleRuleType) {
            if (styleRuleType === StyleRuleTypeEnum.NODATA || styleRuleType === StyleRuleTypeEnum.CLUSTER || styleRuleType === StyleRuleTypeEnum.SIMPLE) {
                if (this.getBackgroundColour() && !verifyColour(this.getBackgroundColour())) {
                    errors.push(this.getLogId() + ': invalid backgroundColor -> ' + this.getBackgroundColour());
                }

                if (this.getBorderColour() && !verifyColour(this.getBorderColour())) {
                    errors.push(this.getLogId() + ': invalid borderColor -> ' + this.getBorderColour());
                }

                if (this.getBorderWidth() != null && (!isNumber(this.getBorderWidth() || this.getBorderWidth() < 0))) {
                    errors.push(this.getLogId() + ': invalid borderWidth -> ' + this.getBorderWidth());
                }

                if (this.getWidth() !== undefined && (!isNumber(this.getWidth() || this.getWidth() < 0))) {
                    errors.push(this.getLogId() + ': invalid width -> ' + this.getWidth());
                }

                if (this.getRadius() !== undefined && (!isNumber(this.getRadius() || this.getRadius() < 0))) {
                    errors.push(this.getLogId() + ': invalid radius -> ' + this.getRadius());
                }

                const fill = this.getFillPattern();
                if (fill) {
                    const fillErrors = fill.verify();
                    for (let fe in fillErrors) {
                        errors.push(this.getLogId() + fillErrors[fe]);
                    }
                }
            }

            if (styleRuleType === StyleRuleTypeEnum.CLASSIFIED) {
                const classes = this.getClasses();
                if (classes.length === 0) {
                    errors.push(this.getLogId() + ": classes must not be empty");
                } else {
                    let values = {};
                    for (let c in classes) {
                        errors = errors.concat(classes[c].verify(c).map(e=>this.getLogId()+': '+e));
                        if (values[classes[c].getValue()] === 'exists') {
                            errors.push(this.getLogId() + ': Value ' + classes[c].getValue() + ' repeated on ' + classes[c].getLogId());
                        }
                    }
                }
            }

            if (styleRuleType === StyleRuleTypeEnum.DISCRETE) {
                const buckets = this.getBuckets();
                if (buckets.length === 0) {
                    errors.push(this.getLogId() + ": buckets must not be empty");
                } else {
                    for (let b in buckets) {
                        const bucket = buckets[b];
                        errors = errors.concat(bucket.verify(b).map(e=>this.getLogId()+': '+e));
                        const overlapsWith = (bucket2Test, index) => {
                            const res = [];
                            const overlap = (b1, b2) => {
                                if (b1.verify().length === 0 && b2.verify().length === 0) {
                                    const b1Max = b1.getMaxValue() !== undefined ? b1.getMaxValue() : Number.MAX_VALUE;
                                    const b1Min = b1.getMinValue() !== undefined ? b1.getMinValue() : Number.MAX_VALUE*-1;
                                    const b2Max = b2.getMaxValue() !== undefined ? b2.getMaxValue() : Number.MAX_VALUE;
                                    const b2Min = b2.getMinValue() !== undefined ? b2.getMinValue() : Number.MAX_VALUE*-1;
                                    // if(b1Min > b2Min && b1Min < b2Max || b2Min > b1Min && b2Min < b1Max)
                                    //     debugger
                                    return b1Min > b2Min && b1Min < b2Max || b2Min > b1Min && b2Min < b1Max;
                                }
                            }

                            for (let b in buckets) {
                                if (b !== index && overlap(bucket2Test, buckets[b]))
                                    res.push(b);
                            }
                            return res;
                        };
                        const overlappedIndexes = overlapsWith(bucket, b);
                        if (overlappedIndexes.length > 0) {
                            errors.push(this.getLogId() + ': Bucket ' + b + ' overlaps with buckets at positions ' + JSON.stringify(overlappedIndexes));
                        }
                    }
                }
            }

            if (styleRuleType === StyleRuleTypeEnum.CONTINUOUS) {
                const ranges = this.getRanges();
                if (ranges.length === 0) {
                    errors.push(this.getLogId() + ": ranges must not be empty");
                } else {
                    const rangesOverlap = (r1,r2)=>{
                        const min1 = r1.getMinValue()===null?undefined:r1.getMinValue();
                        const min2 = r2.getMinValue()===null?undefined:r2.getMinValue();
                        const max1 = r1.getMaxValue()===null?undefined:r1.getMaxValue();
                        const max2 = r2.getMaxValue()===null?undefined:r2.getMaxValue();
                        if(min1===undefined) return min2 === undefined || max1 > min2
                        if(min2===undefined) return max2 > min1;
                        if(max1===undefined) return max2 === undefined || min1 < max2;
                        if(max2===undefined) return min2 < max1;
                        return !(max1<=min2 || max2<=min1) ;
                    }
                    const overlaps = {}
                    for (let r1 in ranges) {
                        errors = errors.concat(ranges[r1].verify(r1).map(e=>this.getLogId()+': '+e));
                        const rangeIndex1 = Number(r1);
                        for (let r2 in ranges) {
                            const rangeIndex2 = Number(r2);
                            if(rangeIndex1!==rangeIndex2 && rangesOverlap(ranges[rangeIndex1],ranges[rangeIndex2]))
                                overlaps[Math.min(rangeIndex1,rangeIndex2)+"-"+Math.max(rangeIndex1,rangeIndex2)] = [Math.min(rangeIndex1,rangeIndex2),Math.max(rangeIndex1,rangeIndex2)];
                        }
                    }
                    Object.keys(overlaps).forEach(k=>errors.push(this.getLogId() + ': Ranges overlap at positions ' + (overlaps[k][0]) + ' and ' + overlaps[k][1]))
                }
            }

            if (styleRuleType === StyleRuleTypeEnum.SIMPLE) {
                if (this.getImageUrl() !== undefined && this.getImageUrl().length === 0) {
                    errors.push(this.getLogId() + ': invalid imageUrl -> ' + this.getImageUrl());
                }
            }

            if (styleRuleType === StyleRuleTypeEnum.EXTERNAL_DYNAMIC_IMAGE
                || styleRuleType === StyleRuleTypeEnum.EXTERNAL_IMAGE
            || styleRuleType === StyleRuleTypeEnum.ZOOM_DEPENDENT_EXTERNAL_DYNAMIC_IMAGE) {
                const urls = styleRuleType === StyleRuleTypeEnum.ZOOM_DEPENDENT_EXTERNAL_DYNAMIC_IMAGE
                ?this.getImageUrls().map(iuzl=>prefixUrl(iuzl.imageUrl))
                    :[this.getImageUrl()];
                urls.forEach(url=>{
                    if (!url || url.length === 0) {
                        errors.push(this.getLogId() + ': imageUrl is mandatory.');
                    } else if (url.length === 0) {
                        errors.push(this.getLogId() + ': invalid imageUrl -> ' + url);
                    }
                });
                if (styleRuleType === StyleRuleTypeEnum.ZOOM_DEPENDENT_EXTERNAL_DYNAMIC_IMAGE) {
                    this.getImageUrls().forEach(iuzl=>{
                        if(!iuzl.zoomLevels || iuzl.zoomLevels.length===0){
                            errors.push(this.getLogId() + ': zoomLevels is mandatory.');
                        } else {
                            iuzl.zoomLevels.forEach(zl=>{
                                if(!isInteger(zl))
                                    errors.push(this.getLogId() + ': zoomLevels must be integers.');
                            })
                        }
                    });
                }
            }
        }

        return errors;
    }
}

/**
 * Any rule entry
 * @extends Config
 * @hideconstructor
 */
class AbstractRuleEntry extends Config {

    getClassName() {
        return "AbstractRuleEntry";
    }

    /**
     * A CSS colour for the border
     * @return {string}
     */
    getBorderColour() {
        return this.config.borderColor;
    }

    /**
     * The width of the border in pixels
     * @return {number}
     */
    getBorderWidth() {
        return this.config.borderWidth;
    }

    /**
     * The radius for point geometries
     * @return {undefined|number}
     */
    getRadius() {
        return this.config.radius === undefined ? undefined : Math.min(50, Math.max(2, this.config.radius));
    }

    /**
     * The width for line geometries
     * @return {undefined|number}
     */
    getWidth() {
        return this.config.width === undefined ? undefined : Math.min(20, Math.max(1, this.config.width));
    }

    /**
     *
     * @param i {number|undefined} an integer index of this rule entry
     * @returns {Array<string>}
     */
    verify(i=undefined) {
        const errors = super.verify();
        const indexStr = i !== undefined ? ("[" + i + "]") : "";
        if (this.getBorderColour() && !verifyColour(this.getBorderColour())) {
            errors.push(this.getLogId() + indexStr +': invalid borderColor -> ' + this.getBorderColour());
        }

        if (this.getBorderWidth() != null && (!isNumber(this.getBorderWidth() || this.getBorderWidth() < 0))) {
            errors.push(this.getLogId() + ': invalid borderWidth -> ' + this.getBorderWidth());
        }

        if (this.getWidth() !== undefined && (!isNumber(this.getWidth() || this.getWidth() < 0))) {
            errors.push(this.getLogId() + indexStr + ': invalid width -> ' + this.getWidth());
        }

        if (this.getRadius() !== undefined && (!isNumber(this.getRadius() || this.getRadius() < 0))) {
            errors.push(this.getLogId() + indexStr + ': invalid radius -> ' + this.getRadius());
        }

        return errors;
    }
}

/**
 * A fill pattern
 * @extends Config
 * @hideconstructor
 */
class FillPattern extends Config {
    getClassName(){
        return "FillPattern";
    }

    /**
     * The pattern type
     * @return {FillPatternTypeEnum}
     */
    getFillPatternType() {
        return FillPatternTypeEnum.getForCode(this.config.type);
    }

    /**
     * The width of the lines in pixels
     * @return {undefined|number}
     */
    getWidth() {
        return this.config.width === undefined ? undefined : Math.min(5, Math.max(1, this.config.width));
    }

    /**
     * The radius of the dots in pixels
     * @return {undefined|number}
     */
    getRadius() {
        return this.config.radius === undefined ? undefined : Math.min(10, Math.max(2, this.config.radius));
    }

    /**
     * The CSS colour of the dots/lines
     * @return {string}
     */
    getColour() {
        return this.config.color;
    }

    verify() {
        const errors = super.verify();

        if (this.config.type && this.config.type.length > 0 && !FillPatternTypeEnum.exists(this.config.type)) {
            errors.push(this.getLogId() + ': invalid type. Possible values: ' + FillPatternTypeEnum.getPossibleCodes());
        }

        if (this.getColour() && !verifyColour(this.getColour())) {
            errors.push(this.getLogId() + ': invalid color -> ' + this.getColour());
        }

        if (this.getWidth() != null && (!isNumber(this.getWidth() || this.getWidth() < 0))) {
            errors.push(this.getLogId() + ': invalid width -> ' + this.getWidth());
        }

        if (this.getWidth() !== undefined && (!isNumber(this.getWidth() || this.getWidth() < 0))) {
            errors.push(this.getLogId() + ': invalid width -> ' + this.getWidth());
        }

        if (this.getRadius() !== undefined && (!isNumber(this.getRadius() || this.getRadius() < 0))) {
            errors.push(this.getLogId() + ': invalid radius -> ' + this.getRadius());
        }
        return errors;
    }
}

/**
 * A line pattern
 * @extends Config
 * @hideconstructor
 */
class LinePattern extends Config {
    getClassName(){
        return "LinePattern";
    }

    /**
     * The pattern type
     * @return {LinePatternTypeEnum}
     */
    getLinePatternType() {
        return LinePatternTypeEnum.getForCode(this.config.type);
    }

    /**
     * The width of the line in pixels
     * @return {undefined|number}
     */
    getWidth() {
        return this.config.width === undefined ? undefined : Math.min(5, Math.max(1, this.config.width));
    }

    /**
     * The CSS colour of the dots/lines
     * @return {string}
     */
    getColour() {
        return this.config.color;
    }

    verify() {
        const errors = super.verify();

        if (this.config.type && this.config.type.length > 0 && !LinePatternTypeEnum.exists(this.config.type)) {
            errors.push(this.getLogId() + ': invalid type. Possible values: ' + LinePatternTypeEnum.getPossibleCodes());
        }

        if (this.getColour() && !verifyColour(this.getColour())) {
            errors.push(this.getLogId() + ': invalid colour -> ' + this.getColour());
        }

        if (this.getWidth() != null && (!isNumber(this.getWidth() || this.getWidth() < 0))) {
            errors.push(this.getLogId() + ': invalid width -> ' + this.getWidth());
        }

        if (this.getWidth() !== undefined && (!isNumber(this.getWidth() || this.getWidth() < 0))) {
            errors.push(this.getLogId() + ': invalid width -> ' + this.getWidth());
        }
        return errors;
    }
}

/**
 * Any min/max rule entry
 * @extends AbstractRuleEntry
 * @hideconstructor
 */
class AbstractMinMaxRuleEntry extends AbstractRuleEntry {

    /**
     * The min value
     * @return {number}
     */
    getMinValue() {
        return this.config.minValue;
    }

    /**
     * The max value
     * @return {number}
     */
    getMaxValue() {
        return this.config.maxValue;
    }

    verify(i=undefined) {
        const errors = super.verify(i);
        const indexStr = i !== undefined ? ("[" + i + "]") : "";
        if (this.getMinValue() === undefined && this.getMaxValue() === undefined) {
            errors.push(this.getLogId() + indexStr + ': one of minValue and maxValue is mandatory');
        }

        if (this.getMinValue() !== undefined && !isNumber(this.getMinValue())) {
            errors.push(this.getLogId() +indexStr+ ': invalid minValue -> ' + this.getMinValue());
        }
        if (this.getMaxValue() !== undefined && !isNumber(this.getMaxValue())) {
            errors.push(this.getLogId() +indexStr+ ': invalid maxValue -> ' + this.getMaxValue());
        }

        if (this.getMinValue() !== undefined && this.getMaxValue() !== undefined) {
            if (this.getMinValue() > this.getMaxValue()) {
                errors.push(this.getLogId() +indexStr+ ': invalid minValue/maxValue -> (min <> max) ' + this.getMinValue() + ' <> ' + this.getMaxValue());
            }
        }

        return errors;
    }
}

/**
 * A bucket for DISCRETE rules
 * @extends AbstractMinMaxRuleEntry
 * @hideconstructor
 */
class Bucket extends AbstractMinMaxRuleEntry {

    getClassName(){
        return "Bucket";
    }

    /**
     * A CSS colour for the bucket's background
     * @return {string}
     */
    getBackgroundColour() {
        return nnTrim(this.config.backgroundColor);
    }

    /**
     * A translatable key for the bucket label
     * @return {string}
     */
    getLabel() {
        return nnTrim(this.config.label);
    }

    /**
     * A translatable key for the bucket minimum label
     * @return {string}
     */
    getMinLabel() {
        return nnTrim(this.config.minLabel);
    }

    /**
     * A translatable key for the bucket maximum label
     * @return {string}
     */
    getMaxLabel() {
        return nnTrim(this.config.maxLabel);
    }

    /**
     * A translatable key for the bucket group label
     * @return {string}
     */
    getGroupLabel() {
        return nnTrim(this.config.groupLabel);
    }

    verify(i=undefined) {
        const errors = super.verify(i);
        const indexStr = i !== undefined ? ("[" + i + "]") : "";
        const noMinValue = this.getMinValue() === undefined;
        const noMaxValue = this.getMaxValue() === undefined;
        if (noMinValue && noMaxValue) {
            errors.push(this.getLogId() + indexStr + ': one of minValue and maxValue is mandatory');
        }
        if (this.getBackgroundColour() && !verifyColour(this.getBackgroundColour())) {
            errors.push(this.getLogId() +indexStr + ': invalid backgroundColor -> ' + this.getBackgroundColour());
        }

        return errors;
    }

}

/**
 * A range for CONTINUOUS rules
 * @extends AbstractMinMaxRuleEntry
 * @hideconstructor
 */
class Range extends AbstractMinMaxRuleEntry {
    getClassName(){
        return "Range";
    }

    /**
     * The minimum CSS colour for the range's background
     * @return {string}
     */
    getMinBackgroundColour() {
        return nnTrim(this.config.minBackgroundColor);
    }

    /**
     * The maximum CSS colour for the range's background
     * @return {string}
     */
    getMaxBackgroundColour() {
        return nnTrim(this.config.maxBackgroundColor);
    }

    /**
     * A translatable key for the range minimum label
     * @return {string}
     */
    getMinLabel() {
        return nnTrim(this.config.minLabel);
    }

    /**
     * A translatable key for the range maximum label
     * @return {string}
     */
    getMaxLabel() {
        return nnTrim(this.config.maxLabel);
    }

    /**
     * A translatable key for the bucket label
     * @return {string}
     */
    getLabel() {
        return nnTrim(this.config.label);
    }

    /**
     * A translatable key for the range group label
     * @return {string}
     */
    getGroupLabel() {
        return nnTrim(this.config.groupLabel);
    }

    verify(i=undefined) {
        const errors = super.verify(i);
        const indexStr = i !== undefined ? ("[" + i + "]") : "";

        const noMinValue = this.getMinValue() === undefined;
        const noMaxValue = this.getMaxValue() === undefined;
        if (noMinValue || noMaxValue) {
            errors.push(this.getLogId() +indexStr+ ': both of minValue and maxValue are mandatory');
        }

        if (!this.getMinBackgroundColour()) {
            errors.push(this.getLogId() +indexStr+ ': minBackgroundColor is mandatory');
        } else if (!verifyColour(this.getMinBackgroundColour())) {
            errors.push(this.getLogId() +indexStr+ ': invalid minBackgroundColor -> ' + this.getMinBackgroundColour());
        }

        if (!this.getMaxBackgroundColour()) {
            errors.push(this.getLogId() +indexStr+ ': maxBackgroundColor is mandatory');
        } else if (!verifyColour(this.getMaxBackgroundColour())) {
            errors.push(this.getLogId() +indexStr+ ': invalid maxBackgroundColor -> ' + this.getMaxBackgroundColour());
        }

        return errors;
    }
}

/**
 * A class for CLASSIFIED rules
 * @extends AbstractRuleEntry
 * @hideconstructor
 */
class Clazz extends AbstractRuleEntry {
    getClassName(){
        return "Clazz";
    }

    /**
     * A CSS colour for the class's background
     * @return {string}
     */
    getBackgroundColour() {
        return nnTrim(this.config.backgroundColor);
    }

    /**
     * A translatable key for the class label
     * @return {string}
     */
    getLabel() {
        return nnTrim(this.config.label);
    }

    /**
     * A translatable key for the class group label
     * @return {string}
     */
    getGroupLabel() {
        return nnTrim(this.config.groupLabel);
    }

    /**
     * This class's value
     * @return {string}
     */
    getValue() {
        return this.config.value !== undefined && this.config.value !== null
            ? ('' + this.config.value)
            : undefined;
    }

    getFillPattern() {
        return nnTrim(this.config.fill) === undefined ? undefined : new FillPattern(this.config.fill);
    }


    getLinePattern() {
        return nnTrim(this.config.linePattern) === undefined ? undefined : new LinePattern(this.config.linePattern);
    }

    verify(i=undefined) {
        let errors = super.verify(i);
        const indexStr = i !== undefined ? ("[" + i + "]") : "";

        if (this.getBackgroundColour() && !verifyColour(this.getBackgroundColour())) {
            errors.push(this.getLogId() +indexStr+ ': invalid backgroundColor -> ' + this.getBackgroundColour());
        }

        if (this.getLinePattern()) {
            errors = errors.concat(this.getLinePattern().verify().map(e=>this.getLogId() +indexStr+ ': '+e));
        }

        if (this.getFillPattern()) {
            errors = errors.concat(this.getFillPattern().verify().map(e=>this.getLogId() +indexStr+ ': '+e));
        }

        return errors;
    }
}

export {StyleConfig}